// Reports > Campaigns > Performance > "Strength (ad campaign)".
//
// Backed by GET /api/reports/campaigns/strength. Rows are AD CAMPAIGNS folded
// through the campaign groups (the ROI / Spend report's axis, so cost joins
// cleanly); the dashboard's Campaign & Source card groups by DEVELOPMENT and is
// not this table. Each row expands to its development breakdown, a weekly
// sparkline and its leads.
//
// This file owns no business rule: stage counts, cost and first-contact all come
// from the server, which takes the stages from the same sets the dashboard tiles
// read. The percentages, CPL (cost / costLeads) and CPD (cost / costDeals) are
// arithmetic on those numbers. costLeads / costDeals are the leads and deals of
// the months that HAVE spend entered, so a half-priced window never divides part
// of the money by all of the leads.
//
// Everything App.js-scoped (colours, Card, Select, apiFetch, loadXLSX) arrives in
// the `ui` prop: importing it from App.js would be a circular import.
import { useState, useEffect, useMemo } from "react";
import { peekReportReturn, clearReportReturn } from "../lib/reportReturn";
import DashRangePicker from "../components/DashRangePicker";
import { periodChipStyle, PERIOD_ACCENT_BLUE } from "../lib/periodChip";
import { campaignStrengthDateChip } from "../lib/dateChipSites";

var SPEND_PLATFORMS = [
  { value: "meta", label: "Meta (Facebook + Instagram)" }, { value: "google", label: "Google Ads" },
  { value: "tiktok", label: "TikTok" }, { value: "snapchat", label: "Snapchat" },
  { value: "linkedin", label: "LinkedIn" }, { value: "other", label: "Other" }
];
// Campaign names were captured from this day. Used ONLY for the info line: this view
// offers every period and clamps nothing (owner ruling 2026-10-11).
var TAGGING_START = "2026-07-01";
var pad2 = function (n) { return (n < 10 ? "0" : "") + n; };
var ymd = function (d) { return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()); };
var startOf = function (s) { var p = s.split("-"); return new Date(+p[0], +p[1] - 1, +p[2], 0, 0, 0, 0).getTime(); };
var endOf = function (s) { var p = s.split("-"); return new Date(+p[0], +p[1] - 1, +p[2], 23, 59, 59, 999).getTime(); };
var monthLabel = function (m) {
  var p = String(m || "").split("-");
  try { return new Date(+p[0], +p[1] - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }); } catch (e) { return m; }
};
var nf = function (v) { return v == null ? "—" : Math.round(v).toLocaleString("en-US"); };
var pct = function (n, base) {
  if (!base) return "—";
  var v = n / base * 100;
  return (v >= 10 || v === 0 ? Math.round(v) : Math.round(v * 10) / 10) + "%";
};
var hm = function (min) { return min == null ? "—" : Math.floor(min / 60) + ":" + pad2(Math.round(min % 60)); };
var safeDiv = function (a, b) { return (a == null || !b) ? null : a / b; };
var cplOf = function (r) { return r.cost == null ? null : safeDiv(r.cost, r.costLeads); };
var cpdOf = function (r) { return r.cost == null ? null : safeDiv(r.cost, r.costDeals); };
var strengthOf = function (r) { return r.leads ? r.deals / r.leads * 100 : 0; };
// Bands are the dashboard Quality chip's LONG-range conversion cutoffs (>=3 High, >=1 Medium).
var band = function (v) { return v >= 3 ? { w: "High", c: "#16A34A", bg: "#DCFCE7" } : v >= 1 ? { w: "Medium", c: "#B45309", bg: "#FEF3C7" } : { w: "Low", c: "#DC2626", bg: "#FEE2E2" }; };

// Sort accessors: null sinks to the bottom whichever way the column sorts.
var SORT = {
  label: function (r) { return String(r.label || "").toLowerCase(); },
  leads: function (r) { return r.leads; }, interested: function (r) { return r.interested; },
  meetings: function (r) { return r.meetings; }, eoi: function (r) { return r.eoi; }, deals: function (r) { return r.deals; },
  dealValue: function (r) { return r.dealValue; },
  ni: function (r) { return r.leads ? r.notInterested / r.leads : null; }, na: function (r) { return r.leads ? r.noAnswer / r.leads : null; },
  fc: function (r) { return r.avgFirstContactMinutes; }, cost: function (r) { return r.cost; },
  cpl: cplOf, cpd: cpdOf,
  strength: function (r) { return r.unset ? null : strengthOf(r); }   // 'No ad campaign' is not a campaign: no strength
};

function Spark(p) {
  var w = p.weekly || [];
  if (w.length < 2) return <div style={{ fontSize: 11, color: "#94A3B8" }}>Not enough weeks for a trend.</div>;
  var W = 220, H = 44, max = 1;
  w.forEach(function (x) { max = Math.max(max, x.leads, x.meetings, x.deals); });
  var line = function (key) {
    return w.map(function (x, i) { return (i * (W / (w.length - 1))).toFixed(1) + "," + (H - 3 - (x[key] / max) * (H - 8)).toFixed(1); }).join(" ");
  };
  return <div>
    <svg width={W} height={H} style={{ display: "block" }}>
      <polyline points={line("leads")} fill="none" stroke="#3B82F6" strokeWidth="1.8" />
      <polyline points={line("meetings")} fill="none" stroke="#F59E0B" strokeWidth="1.8" />
      <polyline points={line("deals")} fill="none" stroke="#16A34A" strokeWidth="1.8" />
    </svg>
    <div style={{ fontSize: 10.5, color: "#64748B", marginTop: 3 }}>
      <span style={{ color: "#3B82F6" }}>● leads</span>&nbsp;&nbsp;<span style={{ color: "#F59E0B" }}>● meetings</span>&nbsp;&nbsp;<span style={{ color: "#16A34A" }}>● deals</span>
      &nbsp;· {w.length} weeks (Sat→Fri, by lead-creation week)
    </div>
  </div>;
}

export function ReportsCampaignStrength(p) {
  var ui = p.ui, C = ui.C, Card = ui.Card, Select = ui.Select, apiFetch = ui.apiFetch;
  var now = new Date();
  var canEditCost = !!(p.cu && (p.cu.role === "admin" || p.cu.role === "operation"));
  // Period controls are the app's standard set (All · Q1–Q4 · month · year · from/to).
  // `from`/`to` are the truth; the chips and selects only write them and light when
  // the dates ARE their range. Nothing is removed or clamped.
  // Coming back from a drill-down ("← Back to report" on the Leads page): the filters and
  // sort the report was left in. Read once; null on any ordinary visit.
  var rr0 = peekReportReturn("performance-strength") || {};
  useEffect(function () { clearReportReturn(); }, []);
  var [from, setFrom] = useState(rr0.from || ymd(new Date(now.getFullYear(), now.getMonth(), 1)));
  var [to, setTo] = useState(rr0.to || ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
  var [year, setYear] = useState(rr0.year != null ? rr0.year : now.getFullYear());
  var [source, setSource] = useState(rr0.source || "all");
  var [project, setProject] = useState(rr0.project || "all");
  var [who, setWho] = useState(rr0.who || "all");
  var [compare, setCompare] = useState(!!rr0.compare);
  var [sortKey, setSortKey] = useState(rr0.sortKey || "strength");
  var [sortDir, setSortDir] = useState(rr0.sortDir || -1);
  var [state, setState] = useState({ loading: true, data: null, error: null });
  var [open, setOpen] = useState(null);
  var [showUngrouped, setShowUngrouped] = useState(true);
  var [costRow, setCostRow] = useState(null);
  var [exporting, setExporting] = useState(false);
  var [bump, setBump] = useState(0);
  var [devList, setDevList] = useState([]);
  useEffect(function () {
    var aborted = false;
    apiFetch("/api/reports/campaigns/performance?from=0&to=" + Date.now(), "GET", null, p.token)
      .then(function (d) {
        if (aborted) return;
        var names = (d.campaigns || []).filter(function (c) { return !c.manual && c.campaign; }).map(function (c) { return c.campaign; });
        setDevList(names.sort(function (a, b) { return a.toLowerCase() < b.toLowerCase() ? -1 : 1; }));
      })
      .catch(function () { /* the All option still works; the list fills on the next open */ });
    return function () { aborted = true; };
  }, [p.token]);

  var who2 = who.split(":");
  var qs = "from=" + startOf(from) + "&to=" + endOf(to) + "&trend=weekly"
    + (compare ? "&compare=prev" : "")
    + (source !== "all" ? "&source=" + encodeURIComponent(source) : "")
    + (project !== "all" ? "&project=" + encodeURIComponent(project) : "")
    + (who2[0] === "team" ? "&team=" + who2[1] : "") + (who2[0] === "agent" ? "&agentId=" + who2[1] : "");

  useEffect(function () {
    var aborted = false;
    setState(function (s) { return Object.assign({}, s, { loading: true, error: null }); });
    apiFetch("/api/reports/campaigns/strength?" + qs, "GET", null, p.token)
      .then(function (d) {
        if (aborted) return;
        setState({ loading: false, data: d, error: null });
      })
      .catch(function (e) { if (!aborted) setState({ loading: false, data: null, error: (e && e.message) || "Failed to load" }); });
    return function () { aborted = true; };
  }, [qs, bump]);

  var d = state.data, skel = state.loading && !d;
  var rows = (d && d.campaigns) || [];
  var summary = (d && d.summary) || {};
  var months = (d && d.months) || [];
  var comparing = !!(d && d.compare && d.compare.range);

  var sorter = function (a, b) {
    var f = SORT[sortKey] || SORT.strength, x = f(a), y = f(b);
    if (x == null && y == null) return b.leads - a.leads;
    if (x == null) return 1;
    if (y == null) return -1;
    if (x === y) return b.leads - a.leads;
    return (x < y ? -1 : 1) * sortDir;
  };
  var main = useMemo(function () { return rows.filter(function (r) { return r.isGroup; }).sort(sorter); }, [rows, sortKey, sortDir]);
  var ungrouped = useMemo(function () { return rows.filter(function (r) { return !r.isGroup && !r.unset; }).sort(sorter); }, [rows, sortKey, sortDir]);
  var unset = rows.filter(function (r) { return r.unset; })[0] || null;

  var setRange = function (a, b) { setFrom(a); setTo(b); setYear(+a.slice(0, 4)); };
  // sel: "all" | "Q1".."Q4" | "m0".."m11" — a range inside a year.
  var rangeFor = function (sel, y) {
    if (sel === "all") return [ymd(new Date(y, 0, 1)), ymd(new Date(y, 11, 31))];
    if (sel[0] === "Q") { var q = +sel[1] - 1; return [ymd(new Date(y, q * 3, 1)), ymd(new Date(y, q * 3 + 3, 0))]; }
    var m = +sel.slice(1); return [ymd(new Date(y, m, 1)), ymd(new Date(y, m + 1, 0))];
  };
  var SELS = ["all", "Q1", "Q2", "Q3", "Q4"].concat([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(function (m) { return "m" + m; }));
  var selOf = (function () {
    for (var i = 0; i < SELS.length; i++) { var r = rangeFor(SELS[i], year); if (r[0] === from && r[1] === to) return SELS[i]; }
    return "custom";
  })();
  var applySel = function (sel, y) { var r = rangeFor(sel, y); setFrom(r[0]); setTo(r[1]); setYear(y); };
  var thisMonthR = rangeFor("m" + now.getMonth(), now.getFullYear());
  var lastMonthR = (function () { var y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(); return rangeFor("m" + ((now.getMonth() + 11) % 12), y); })();

  var sortBy = function (k) { if (k === sortKey) setSortDir(-sortDir); else { setSortKey(k); setSortDir(k === "label" ? 1 : -1); } };

  // A chip is lit when the picked dates ARE its range (same look as the By-development chips).
  var chip = function (label, fn, a, b) {
    var on = a === from && b === to;
    return <button key={label} type="button" onClick={fn} style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid", borderColor: on ? C.accent : "#E2E8F0", background: on ? C.accent + "26" : "#fff", color: on ? "#92400E" : C.textLight, fontSize: 12, fontWeight: on ? 700 : 600, cursor: "pointer" }}>{label}</button>;
  };
  var yearNow = now.getFullYear();
  var yearOptions = [yearNow, yearNow - 1, yearNow - 2, yearNow - 3].map(function (y) { return { value: String(y), label: String(y) }; });
  var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  var monthOptions = [{ value: "all", label: "All months" }].concat(MONTHS.map(function (m, i) { return { value: String(i), label: m }; }));

  var people = p.users || [];
  var leaderIds = {};
  people.forEach(function (u) { if (u.reportsTo) leaderIds[String(u.reportsTo._id || u.reportsTo)] = 1; });
  var whoOptions = [{ value: "all", label: "All agents" }]
    .concat(people.filter(function (u) { return u.active !== false && leaderIds[String(u._id)]; }).map(function (u) { return { value: "team:" + u._id, label: "Team · " + u.name }; }))
    .concat(people.filter(function (u) { return u.active !== false && u.role === "sales" && !leaderIds[String(u._id)]; }).map(function (u) { return { value: "agent:" + u._id, label: u.name }; }));
  // The Source list is the Reports toolbar's own (REPORTS_ROSTER.sources: the SOURCES
  // catalog ∪ the whole book's distinct sources from /api/filter-options), handed in by
  // ReportsPage - never folded from this view's result rows, which listed only the
  // sources present in the loaded range.
  var sourceOptions = [{ value: "all", label: "All sources" }].concat((p.sourceOptions || []).map(function (s) { return { value: s, label: s }; }));
  // The complete development list, resolved the way the By-development view resolves
  // them: /campaigns/performance over ALL time (development field per lead vintage,
  // spelling variants merged). Fetched once, never from this range's rows, so a
  // development with no leads in the current range is still pickable.
  var projectOptions = [{ value: "all", label: "All developments" }].concat(devList.map(function (s) { return { value: s, label: s }; }));

  var th = { fontSize: 11, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.04em", padding: "8px 8px", borderBottom: "1px solid #E2E8F0", fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", textAlign: "right" };
  var td = { fontSize: 12.5, padding: "8px 8px", borderBottom: "1px solid #F1F5F9", color: C.text, textAlign: "right", verticalAlign: "top", fontVariantNumeric: "tabular-nums" };

  // value + (when comparing) delta vs previous period and a muted previous line.
  var Cell = function (cur, prev, fmt, lowerBetter, sub) {
    var line2 = null;
    if (comparing) {
      var pv = prev;
      if (cur != null && pv != null && cur !== pv) {
        var diff = cur - pv, good = lowerBetter ? diff < 0 : diff > 0;
        line2 = <div style={{ fontSize: 10.5, color: good ? "#16A34A" : "#DC2626" }}>{diff > 0 ? "▲ " : "▼ "}{fmt(Math.abs(diff))}</div>;
      } else if (cur != null && pv != null) {
        line2 = <div style={{ fontSize: 10.5, color: "#94A3B8" }}>＝</div>;
      }
    }
    return <td style={td}>
      <div>{fmt(cur)}{sub ? <span style={{ color: "#94A3B8", fontSize: 11 }}> {sub}</span> : null}</div>
      {line2}
      {comparing && <div style={{ fontSize: 10.5, color: "#94A3B8" }}>prev {prev == null ? "—" : fmt(prev)}</div>}
    </td>;
  };

  var openCost = function (r) { setCostRow(r); };

  // DRILL-DOWN: this row's leads on the Leads page - the report's period, the row's
  // ad-campaign bucket (resolved on the server with the Strength grouping), plus the
  // report's source / development filters. An agent or team filter cannot be carried
  // (the Leads page has no equivalent), so the button is disabled rather than lie.
  var drillDisabled = who !== "all";
  var drill = function (r) {
    if (drillDisabled || !p.setSpecialFilter) return;
    p.setSpecialFilter({
      type: "reportDrill", campaignGroup: r.id,
      devGroup: project !== "all" ? project : undefined, source: source !== "all" ? source : undefined,
      periodFrom: new Date(startOf(from)).toISOString(), periodTo: new Date(endOf(to)).toISOString(), periodLabel: from + " → " + to,
      label: "Ad campaign: " + r.label,
      back: { kind: "performance-strength", tab: "campaigns", view: "performance", mode: "strength",
              state: { from: from, to: to, year: year, source: source, project: project, who: who, compare: compare, sortKey: sortKey, sortDir: sortDir } }
    });
    if (p.setFilter) p.setFilter("all");
    if (p.nav) p.nav("leads");
  };

  var renderRow = function (r) {
    var pv = r.prev, isOpen = open === r.id;
    var st = band(strengthOf(r));
    var ps = pv ? strengthOf(pv) : null;
    var cpl = cplOf(r), cpd = cpdOf(r), pcpl = pv ? cplOf(pv) : null, pcpd = pv ? cpdOf(pv) : null;
    var fmtN = function (v) { return nf(v); };
    var fmtP = function (v) { return v == null ? "—" : (Math.round(v * 10) / 10) + "%"; };
    var costCell = <td style={td}>
      <div>
        {r.cost == null ? <span title={summary.spendComparable === false ? summary.spendNote : (r.isGroup ? "No spend entered for this range" : "Group this campaign in Settings › Campaigns to price it")} style={{ color: "#94A3B8" }}>—</span> : nf(r.cost)}
        {canEditCost && r.isGroup && summary.spendComparable !== false && <span title="Enter spend per month" onClick={function (e) { e.stopPropagation(); openCost(r); }} style={{ cursor: "pointer", marginInlineStart: 6 }}>✏️</span>}
      </div>
      {r.cost != null && r.missingMonths.length > 0 && <div title={"No spend entered for " + r.missingMonths.map(monthLabel).join(", ")} style={{ fontSize: 10.5, color: "#B45309" }}>{r.missingMonths.length} mo. unpriced</div>}
      {comparing && pv && <div style={{ fontSize: 10.5, color: "#94A3B8" }}>prev {pv.cost == null ? "—" : nf(pv.cost)}</div>}
    </td>;
    return [
      <tr key={r.id} onClick={function () { setOpen(isOpen ? null : r.id); }} style={{ cursor: "pointer", background: isOpen ? "#F8FAFC" : "transparent" }}>
        <td style={Object.assign({}, td, { textAlign: "left", fontWeight: 600, whiteSpace: "nowrap", color: r.unset ? C.textLight : C.text, fontStyle: r.unset ? "italic" : "normal" })}>
          <span style={{ color: "#94A3B8", marginInlineEnd: 6 }}>{isOpen ? "▾" : "▸"}</span>{r.label}
          {r.topSource && <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 11 }}> · {r.topSource}</span>}
        </td>
        {Cell(r.leads, pv && pv.leads, fmtN, false)}
        {Cell(r.interested, pv && pv.interested, fmtN, false, pct(r.interested, r.leads))}
        {Cell(r.meetings, pv && pv.meetings, fmtN, false, pct(r.meetings, r.leads))}
        {Cell(r.eoi, pv && pv.eoi, fmtN, false, pct(r.eoi, r.leads))}
        {Cell(r.deals, pv && pv.deals, fmtN, false, pct(r.deals, r.leads))}
        {Cell(r.dealValue, pv && pv.dealValue, fmtN, false)}
        {Cell(r.leads ? r.notInterested / r.leads * 100 : null, pv && pv.leads ? pv.notInterested / pv.leads * 100 : null, fmtP, true)}
        {Cell(r.leads ? r.noAnswer / r.leads * 100 : null, pv && pv.leads ? pv.noAnswer / pv.leads * 100 : null, fmtP, true)}
        {Cell(r.avgFirstContactMinutes, pv && pv.avgFirstContactMinutes, hm, true)}
        {costCell}
        {Cell(cpl, pcpl, fmtN, true)}
        {Cell(cpd, pcpd, fmtN, true)}
        <td style={td}>
          {r.unset
            ? <span title="Not a campaign — leads with the campaign field empty. Strength does not apply." style={{ color: "#94A3B8" }}>—</span>
            : <span title="deals / leads — bands as the dashboard Quality chip: ≥3% High, ≥1% Medium" style={{ background: st.bg, color: st.c, borderRadius: 999, padding: "2px 9px", fontWeight: 700, fontSize: 11.5 }}>{(Math.round(strengthOf(r) * 10) / 10)}% · {st.w}</span>}
          {comparing && !r.unset && <div style={{ fontSize: 10.5, color: "#94A3B8" }}>prev {ps == null ? "—" : (Math.round(ps * 10) / 10) + "%"}</div>}
        </td>
      </tr>,
      isOpen && <tr key={r.id + "x"}><td colSpan={13} style={{ background: "#F8FAFC", padding: "12px 14px", borderBottom: "1px solid #E2E8F0" }}>
        <Expand r={r} qs={qs} ui={ui} token={p.token} months={months} onDrill={drill} drillDisabled={drillDisabled} />
      </td></tr>
    ];
  };

  var Header = function (k, label, left) {
    return <th key={k} onClick={function () { sortBy(k); }} style={Object.assign({}, th, left ? { textAlign: "left" } : {}, { color: sortKey === k ? C.accent : C.textLight })}>{label}{sortKey === k ? (sortDir < 0 ? " ↓" : " ↑") : ""}</th>;
  };

  var runStrengthExport = async function () {
    if (exporting) return;
    setExporting(true);
    try {
      var XLSX = await ui.loadXLSX();
      var res = await apiFetch("/api/reports/campaigns/strength?" + qs, "GET", null, p.token, p.csrfToken);
      var cmp = !!(res && res.compare && res.compare.range);
      var sheet1 = [], sheet2 = [];
      (res.campaigns || []).forEach(function (r) {
        var pv = r.prev, cpl = cplOf(r), cpd = cpdOf(r);
        var o = {
          "Campaign": r.label, "Kind": r.unset ? "No ad campaign" : (r.isGroup ? "Grouped" : "Ungrouped"), "Top source": r.topSource || "",
          "Leads": r.leads, "Potential": r.potential, "Hot case": r.hotCase,
          "Interested": r.interested, "Interested %": pct(r.interested, r.leads),
          "Meetings": r.meetings, "Meetings %": pct(r.meetings, r.leads),
          "EOI": r.eoi, "EOI %": pct(r.eoi, r.leads),
          "Deals": r.deals, "Deals %": pct(r.deals, r.leads), "Deal value (EGP)": r.dealValue,
          "Not interested %": pct(r.notInterested, r.leads), "No answer %": pct(r.noAnswer, r.leads),
          "Avg first contact (h:mm)": hm(r.avgFirstContactMinutes),
          "Cost (EGP)": r.cost == null ? "" : r.cost, "Months without spend": (r.missingMonths || []).map(monthLabel).join(", "),
          "CPL (EGP)": cpl == null ? "" : Math.round(cpl), "CPD (EGP)": cpd == null ? "" : Math.round(cpd),
          "Strength % (deals/leads)": Math.round(strengthOf(r) * 10) / 10
        };
        if (cmp) {
          o["Prev leads"] = pv ? pv.leads : 0; o["Prev interested"] = pv ? pv.interested : 0; o["Prev meetings"] = pv ? pv.meetings : 0;
          o["Prev EOI"] = pv ? pv.eoi : 0; o["Prev deals"] = pv ? pv.deals : 0; o["Prev deal value (EGP)"] = pv ? pv.dealValue : 0;
          o["Prev cost (EGP)"] = pv && pv.cost != null ? pv.cost : "";
          o["Δ leads"] = r.leads - (pv ? pv.leads : 0); o["Δ meetings"] = r.meetings - (pv ? pv.meetings : 0); o["Δ deals"] = r.deals - (pv ? pv.deals : 0);
        }
        sheet1.push(o);
        (r.development || []).forEach(function (x) {
          sheet2.push({ "Campaign": r.label, "Development": x.development || "(none)", "Leads": x.leads, "Interested": x.interested, "Meetings": x.meetings, "EOI": x.eoi, "Deals": x.deals, "Deal value (EGP)": x.dealValue });
        });
      });
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet1), "Campaign strength");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet2, { header: ["Campaign", "Development", "Leads", "Interested", "Meetings", "EOI", "Deals", "Deal value (EGP)"] }), "By development");
      XLSX.writeFile(wb, "campaign_strength_" + from + "_" + to + ".xlsx");
    } catch (e) { alert((e && e.message) || "Export failed"); }
    setExporting(false);
  };

  var mobile = !!p.isMobile;
  var sectionHead = function (text, sub) {
    return <tr key={text}><td colSpan={13} style={{ background: "#FFFBEB", padding: "8px 12px", fontSize: 12, color: "#92400E", borderTop: "1px solid #FDE68A" }}>
      <b>{text}</b> {sub}
    </td></tr>;
  };

  var body = null;
  if (!skel && !state.error && rows.length) {
    if (mobile) {
      body = <div>{main.concat(ungrouped).concat(unset ? [unset] : []).map(function (r) {
        var st = band(strengthOf(r)), cpl = cplOf(r), cpd = cpdOf(r), isOpen = open === r.id;
        return <div key={r.id} style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, marginBottom: 8, background: "#fff" }}>
          <div onClick={function () { setOpen(isOpen ? null : r.id); }} style={{ cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <b style={{ fontSize: 13.5 }}>{r.label}{!r.isGroup && !r.unset ? " (ungrouped)" : ""}</b>
              {r.unset ? <span style={{ color: "#94A3B8" }}>—</span> : <span style={{ background: st.bg, color: st.c, borderRadius: 999, padding: "1px 8px", fontWeight: 700, fontSize: 11 }}>{Math.round(strengthOf(r) * 10) / 10}%</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginTop: 8, fontSize: 12 }}>
              {[["Leads", r.leads], ["Meet", r.meetings], ["EOI", r.eoi], ["Deals", r.deals]].map(function (x) { return <div key={x[0]}><div style={{ color: "#94A3B8", fontSize: 10 }}>{x[0]}</div><b>{x[1]}</b></div>; })}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginTop: 6, fontSize: 12 }}>
              {[["Cost", r.cost == null ? "—" : nf(r.cost)], ["CPL", cpl == null ? "—" : nf(cpl)], ["CPD", cpd == null ? "—" : nf(cpd)], ["1st contact", hm(r.avgFirstContactMinutes)]].map(function (x) { return <div key={x[0]}><div style={{ color: "#94A3B8", fontSize: 10 }}>{x[0]}</div><b>{x[1]}</b></div>; })}
            </div>
            {comparing && r.prev && <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>vs prev: leads {r.leads - r.prev.leads >= 0 ? "▲" : "▼"} {Math.abs(r.leads - r.prev.leads)} · meetings {r.meetings - r.prev.meetings >= 0 ? "▲" : "▼"} {Math.abs(r.meetings - r.prev.meetings)} · deals {r.deals - r.prev.deals >= 0 ? "▲" : "▼"} {Math.abs(r.deals - r.prev.deals)}</div>}
          </div>
          {canEditCost && r.isGroup && summary.spendComparable !== false && <button type="button" onClick={function () { openCost(r); }} style={{ marginTop: 8, fontSize: 12, border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: "4px 10px" }}>✏️ Cost</button>}
          {isOpen && <div style={{ marginTop: 10 }}><Expand r={r} qs={qs} ui={ui} token={p.token} months={months} onDrill={drill} drillDisabled={drillDisabled} /></div>}
        </div>;
      })}</div>;
    } else {
      body = <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1150 }}>
        <thead><tr>
          {Header("label", "Ad campaign", true)}{Header("leads", "Leads")}{Header("interested", "Interested")}{Header("meetings", "Meetings")}{Header("eoi", "EOI")}{Header("deals", "Deals")}
          {Header("dealValue", "Deal value EGP")}{Header("ni", "NI %")}{Header("na", "NA %")}{Header("fc", "1st contact h:mm")}{Header("cost", "Cost EGP")}{Header("cpl", "CPL")}{Header("cpd", "CPD")}{Header("strength", "Strength")}
        </tr></thead>
        <tbody>
          {main.map(renderRow)}
          {ungrouped.length > 0 && <tr key="ung" onClick={function () { setShowUngrouped(!showUngrouped); }} style={{ cursor: "pointer" }}><td colSpan={13} style={{ background: "#FFFBEB", padding: "8px 12px", fontSize: 12, color: "#92400E", borderTop: "1px solid #FDE68A" }}>
            {showUngrouped ? "▾" : "▸"} <b>Ungrouped campaigns</b> — {d.ungrouped.campaigns} campaigns · {d.ungrouped.leads} leads. They cannot be priced until grouped: group them in <b>Settings › Campaigns</b>.
            {d.ungrouped.top.length > 0 && <span> Top: {d.ungrouped.top.map(function (t) { return t.label + " (" + t.leads + ")"; }).join(", ")}.</span>}
            {p.nav && <button type="button" onClick={function (e) { e.stopPropagation(); p.nav("settings"); }} style={{ marginInlineStart: 8, fontSize: 11, border: "1px solid #FCD34D", background: "#fff", borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}>Open Settings</button>}
          </td></tr>}
          {showUngrouped && ungrouped.map(renderRow)}
          {unset && sectionHead("No ad campaign", "— leads with the campaign field empty (Daily Request conversions and manual entries). Not a campaign; shown so the table reconciles to the cohort.")}
          {unset && renderRow(unset)}
        </tbody>
      </table></div>;
    }
  }

  return <div>
    <Card style={{ marginBottom: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {chip("All", function () { applySel("all", year); }, rangeFor("all", year)[0], rangeFor("all", year)[1])}
        {["Q1", "Q2", "Q3", "Q4"].map(function (q) { var r = rangeFor(q, year); return chip(q, function () { applySel(q, year); }, r[0], r[1]); })}
        <Select value={selOf[0] === "m" ? selOf.slice(1) : "all"} onChange={function (e) { var v = e.target.value; if (v === "all") applySel("all", year); else applySel("m" + v, year); }} options={monthOptions} searchable width={130} maxWidth={130} />
        <Select value={String(year)} onChange={function (e) { var y = +e.target.value; applySel(selOf === "custom" ? "all" : selOf, y); }} options={yearOptions} width={90} maxWidth={90} />
        {chip("This month", function () { setRange(thisMonthR[0], thisMonthR[1]); }, thisMonthR[0], thisMonthR[1])}
        {chip("Last month", function () { setRange(lastMonthR[0], lastMonthR[1]); }, lastMonthR[0], lastMonthR[1])}
        {/* 📅 Date (owner 2026-10-31): the shared calendar chip, LAST in the period
            row, in place of the From → To inputs on their own line.
            campaignStrengthDateChip writes the same from / to through setRange. */}
        <DashRangePicker isMobile={mobile} iconOnly={mobile} todayYmd={ymd(now)} min=""
          value={campaignStrengthDateChip.value({ selOf: selOf, from: from, to: to })} active={campaignStrengthDateChip.active({ selOf: selOf })}
          triggerStyle={periodChipStyle(selOf === "custom", PERIOD_ACCENT_BLUE, mobile)}
          onPick={function (a, b) { campaignStrengthDateChip.pick(a, b, { setRange: setRange, thisMonth: thisMonthR }); }} />
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
        <Select value={source} onChange={function (e) { setSource(e.target.value); }} options={sourceOptions} width={150} maxWidth={170} />
        <Select value={project} onChange={function (e) { setProject(e.target.value); }} options={projectOptions} searchable width={190} maxWidth={220} />
        <Select value={who} onChange={function (e) { setWho(e.target.value); }} options={whoOptions} searchable width={190} maxWidth={220} />
        <label style={{ fontSize: 12, color: C.textLight, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
          <input type="checkbox" checked={compare} onChange={function (e) { setCompare(e.target.checked); }} /> Compare with previous period
        </label>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={runStrengthExport} disabled={exporting || skel} style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: exporting ? "default" : "pointer", opacity: exporting ? 0.6 : 1 }}>{exporting ? "Exporting…" : "Export Excel"}</button>
      </div>
    </Card>

    <Card style={{ padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Campaign strength</div>
        {d && <div style={{ fontSize: 11, color: C.textLight }}>{main.length} campaigns · {summary.totalLeads} leads · {summary.totalDeals} deals · {nf(summary.totalDealValue)} EGP</div>}
      </div>
      <div style={{ fontSize: 11, color: "#475569", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
        Grouped by <b>ad campaign</b> (campaign groups). The dashboard's Campaign &amp; Source card groups by <b>development</b>, so the same name can show a different number there — expand a row to see its developments.
        Cohort: leads created in the range; Interested / Meetings / Deals are the dashboard tiles' sets.
      </div>
      {from < TAGGING_START && <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6 }}>ⓘ Campaign tagging began 1 Jul 2026 — leads created earlier have no campaign name, so they appear under “No ad campaign”.</div>}
      {d && d.compare && d.compare.note && <div style={{ fontSize: 11, color: "#92400E", marginBottom: 6 }}>⚠ {d.compare.note}</div>}
      {d && d.monthsWithoutSpend && d.monthsWithoutSpend.length > 0 && summary.spendComparable !== false &&
        <div style={{ fontSize: 11, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
          No spend entered for any campaign in: <b>{d.monthsWithoutSpend.map(monthLabel).join(", ")}</b>. CPL / CPD use only the months that have spend; use ✏️ on a row to enter a month.
        </div>}
      {summary.spendNote && <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8 }}>ⓘ {summary.spendNote}</div>}
      {state.error && <div style={{ fontSize: 12, color: "#DC2626", padding: "8px 0" }}>Couldn't load: {state.error}</div>}
      {skel && !state.error && [0, 1, 2, 3, 4].map(function (i) { return <div key={i} style={{ height: 34, marginBottom: 6, background: "#F1F5F9", borderRadius: 6 }} />; })}
      {!skel && !state.error && rows.length === 0 && <div style={{ fontSize: 12, color: C.textLight, padding: "28px 8px", textAlign: "center" }}>No leads in this range.</div>}
      {body}
    </Card>

    {costRow && <CostModal r={costRow} months={months} ui={ui} token={p.token} csrfToken={p.csrfToken}
      onClose={function () { setCostRow(null); }} onSaved={function () { setCostRow(null); setBump(bump + 1); }} />}
  </div>;
}

function Expand(p) {
  var r = p.r, C = p.ui.C;
  var [leads, setLeads] = useState({ loading: true, rows: [], total: 0, error: null });
  var [more, setMore] = useState(50);
  useEffect(function () {
    var aborted = false;
    p.ui.apiFetch("/api/reports/campaigns/strength?" + p.qs + "&detail=1&campaign=" + encodeURIComponent(r.id), "GET", null, p.token)
      .then(function (d) { if (!aborted) setLeads({ loading: false, rows: d.rows || [], total: d.total || 0, error: null }); })
      .catch(function (e) { if (!aborted) setLeads({ loading: false, rows: [], total: 0, error: (e && e.message) || "Failed" }); });
    return function () { aborted = true; };
  }, [r.id, p.qs]);
  var s = { fontSize: 11.5, padding: "5px 8px", borderBottom: "1px solid #E2E8F0", textAlign: "left" };
  var fmtD = function (v) { try { return v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"; } catch (e) { return "—"; } };
  return <div>
    <div style={{ marginBottom: 10 }}>
      <button type="button" disabled={p.drillDisabled || !p.onDrill} onClick={function () { p.onDrill(r); }}
        title={p.drillDisabled ? "Not available with an agent / team filter" : "Open these " + r.leads + " leads on the Leads page"}
        style={{ border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: p.drillDisabled ? "not-allowed" : "pointer", opacity: p.drillDisabled ? 0.5 : 1 }}>Open {r.leads} in Leads ↗</button>
    </div>
    <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 12 }}>
      <div><div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>WEEKLY TREND</div><Spark weekly={r.weekly} /></div>
      <div><div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>SPEND BY MONTH</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(r.months || []).map(function (m) {
            return <span key={m.month} style={{ fontSize: 11, borderRadius: 8, padding: "3px 8px", border: "1px solid", borderColor: m.spend == null ? "#FCD34D" : "#BBF7D0", background: m.spend == null ? "#FFFBEB" : "#F0FDF4", color: m.spend == null ? "#92400E" : "#166534" }}>
              {monthLabel(m.month)}: {m.spend == null ? "no spend entered" : nf(m.spend) + " EGP"}
            </span>;
          })}
          {(!r.months || !r.months.length) && <span style={{ fontSize: 11, color: "#94A3B8" }}>—</span>}
        </div>
      </div>
    </div>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>BY DEVELOPMENT (project)</div>
    <div style={{ overflowX: "auto", marginBottom: 12 }}><table style={{ borderCollapse: "collapse", minWidth: 480 }}>
      <thead><tr>{["Development", "Leads", "Interested", "Meetings", "EOI", "Deals", "Deal value"].map(function (h) { return <th key={h} style={Object.assign({}, s, { color: C.textLight, fontWeight: 600 })}>{h}</th>; })}</tr></thead>
      <tbody>{(r.development || []).map(function (x) {
        return <tr key={x.development || "none"}><td style={s}>{x.development || "(none)"}</td><td style={s}>{x.leads}</td><td style={s}>{x.interested}</td><td style={s}>{x.meetings}</td><td style={s}>{x.eoi}</td><td style={s}>{x.deals}</td><td style={s}>{nf(x.dealValue)}</td></tr>;
      })}</tbody>
    </table></div>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>LEADS {leads.loading ? "" : "(" + leads.total + ")"}</div>
    {leads.loading && <div style={{ fontSize: 12, color: C.textLight }}>Loading…</div>}
    {leads.error && <div style={{ fontSize: 12, color: "#DC2626" }}>{leads.error}</div>}
    {!leads.loading && !leads.error && <div style={{ overflowX: "auto" }}><table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
      <thead><tr>{["Name", "Status", "Agent", "Development", "Last feedback", "Created", "Last activity"].map(function (h) { return <th key={h} style={Object.assign({}, s, { color: C.textLight, fontWeight: 600 })}>{h}</th>; })}</tr></thead>
      <tbody>{leads.rows.slice(0, more).map(function (l) {
        return <tr key={l._id}><td style={s}>{l.name}{l.deal ? " 🤝" : l.meeting ? " 📅" : ""}</td><td style={s}>{l.status}</td><td style={s}>{l.agent || "—"}</td><td style={s}>{l.development || "—"}</td>
          <td style={Object.assign({}, s, { maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })} title={l.lastFeedback}>{l.lastFeedback || "—"}</td><td style={s}>{fmtD(l.createdAt)}</td><td style={s}>{fmtD(l.lastActivityTime)}</td></tr>;
      })}</tbody>
    </table>
      {leads.rows.length > more && <button type="button" onClick={function () { setMore(more + 100); }} style={{ marginTop: 8, fontSize: 12, border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}>Show more ({leads.rows.length - more} left)</button>}
    </div>}
  </div>;
}

// Cost entry. Goes through the EXISTING PUT /api/settings/campaign-spend (one
// platform per save, one month per request, audit-logged server-side); this
// component never computes or stores money anywhere else.
function CostModal(p) {
  var r = p.r, C = p.ui.C, apiFetch = p.ui.apiFetch;
  var [platform, setPlatform] = useState("meta");
  var [vals, setVals] = useState({});
  var [orig, setOrig] = useState({});
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState("");
  var [saving, setSaving] = useState(false);
  useEffect(function () {
    var aborted = false;
    setLoading(true); setErr("");
    Promise.all(p.months.map(function (m) {
      return apiFetch("/api/settings/campaigns?month=" + m + "&platform=" + platform, "GET", null, p.token)
        .then(function (d) {
          var row = ((d && d.rows) || []).filter(function (x) { return x.groupId === r.groupId; })[0];
          return [m, row && row.spendForPlatform != null ? String(row.spendForPlatform) : ""];
        });
    })).then(function (pairs) {
      if (aborted) return;
      var o = {}; pairs.forEach(function (x) { o[x[0]] = x[1]; });
      setOrig(o); setVals(Object.assign({}, o)); setLoading(false);
    }).catch(function (e) { if (!aborted) { setErr((e && e.message) || "Failed to load current values"); setLoading(false); } });
    return function () { aborted = true; };
  }, [platform]);
  var save = async function () {
    setSaving(true); setErr("");
    try {
      for (var i = 0; i < p.months.length; i++) {
        var m = p.months[i];
        if (String(vals[m] == null ? "" : vals[m]).trim() === String(orig[m] == null ? "" : orig[m]).trim()) continue;
        await apiFetch("/api/settings/campaign-spend", "PUT", { month: m, platform: platform, cells: [{ groupId: r.groupId, spend: String(vals[m]).trim() === "" ? null : Number(vals[m]) }] }, p.token, p.csrfToken);
      }
      p.onSaved();
    } catch (e) { setErr((e && e.message) || "Save failed"); setSaving(false); }
  };
  var Sel = p.ui.Select;
  return <div onClick={p.onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div onClick={function (e) { e.stopPropagation(); }} style={{ background: "#fff", borderRadius: 14, padding: 20, width: 420, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto" }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>Ad spend — {r.label}</div>
      <div style={{ fontSize: 11.5, color: C.textLight, marginBottom: 12 }}>Enter what was spent per month (EGP) for one platform at a time. Leave a month empty if nothing is entered yet — empty is "not entered", not zero.</div>
      <Sel value={platform} onChange={function (e) { setPlatform(e.target.value); }} options={SPEND_PLATFORMS} width={260} maxWidth={300} />
      <div style={{ marginTop: 12 }}>
        {p.months.map(function (m) {
          return <div key={m} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ width: 70, fontSize: 13, fontWeight: 600 }}>{monthLabel(m)}</div>
            <input type="number" min="0" step="any" disabled={loading || saving} value={vals[m] == null ? "" : vals[m]} placeholder={loading ? "…" : "not entered"}
              onChange={function (e) { var v = e.target.value; setVals(function (s) { var n = Object.assign({}, s); n[m] = v; return n; }); }}
              style={{ flex: 1, border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 10px", fontSize: 13 }} />
            <span style={{ fontSize: 11, color: "#94A3B8" }}>EGP</span>
          </div>;
        })}
      </div>
      {err && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button type="button" onClick={p.onClose} style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
        <button type="button" onClick={save} disabled={loading || saving} style={{ border: "none", background: C.accent, color: "#fff", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (loading || saving) ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  </div>;
}

// The Performance sub-view's switch: the new strength table (default) or the
// existing development-axis table, passed in as `legacy`.
export function ReportsCampaignPerformanceSwitch(p) {
  var [mode, setMode] = useState(function () {
    try { var v = localStorage.getItem("crm_reports_campaign_perf_mode"); return v === "development" ? "development" : "strength"; } catch (e) { return "strength"; }
  });
  var pick = function (m) { setMode(m); try { localStorage.setItem("crm_reports_campaign_perf_mode", m); } catch (e) {} };
  var C = p.ui.C;
  var tab = function (id, label) {
    var on = mode === id;
    return <button key={id} type="button" onClick={function () { pick(id); }} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid", borderColor: on ? C.accent : "#E2E8F0", background: on ? C.accent + "12" : "#fff", color: on ? C.accent : C.textLight, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>;
  };
  return <div>
    <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
      {tab("strength", "Strength · by ad campaign")}{tab("development", "By development")}
    </div>
    {mode === "strength" ? <ReportsCampaignStrength {...p} /> : p.legacy}
  </div>;
}
